import type { Ref } from "react";
import { Alert } from "@common/Alert";
import { ComponentColor } from "@common/types";
import type { MarkProcessedResult } from "../_helpers/markProcessedRun";

interface MarkProcessedResultsProps {
    results: MarkProcessedResult[];
    onDismiss: () => void;
    // The region, focusable from script (tabIndex -1): the run's dialog
    // returns focus here when it closes.
    ref?: Ref<HTMLDivElement>;
}

const OUTCOME_LABEL = {
    notProcessed: "Not processed",
    failed: "Failed",
} as const;

// What a bulk Mark Processed did to each selected order (#473). Green only
// when every order was marked; yellow when some were not; red when any failed.
// Every order that was not marked is listed with its reason.
export const MarkProcessedResults = ({
    results,
    onDismiss,
    ref,
}: MarkProcessedResultsProps) => {
    const processed = results.filter(
        (result) => result.outcome === "processed",
    ).length;
    const color = results.some((result) => result.outcome === "failed")
        ? ComponentColor.Error
        : processed < results.length
            ? ComponentColor.Warning
            : ComponentColor.Success;

    return (
        <div
            ref={ref}
            tabIndex={-1}
            role="region"
            aria-label="Mark Processed results"
            className="mt-4 focus:outline-none"
        >
            <Alert
                color={color}
                title={`${processed} of ${results.length} ${results.length === 1 ? "order" : "orders"} marked Processed`}
                onClose={onDismiss}
            >
                {processed < results.length && (
                    <ul className="mt-1 space-y-1">
                        {results.map((result) =>
                            result.outcome === "processed" ? null : (
                                <li key={result.orderId}>
                                    <span className="font-bold">
                                        {result.orderNumber}
                                    </span>
                                    {` — ${OUTCOME_LABEL[result.outcome]}: ${result.reason}`}
                                </li>
                            ),
                        )}
                    </ul>
                )}
            </Alert>
        </div>
    );
};
